import React from "react";
import { Box, Button, Chip, Checkbox, FormControlLabel, IconButton, TextField, Typography, Tooltip, Alert, Tabs, Tab } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import UploadIcon from "@mui/icons-material/Upload";
import { enqueueSnackbar } from "notistack";
import api from "../Auth/Api";
import globals from "../Globals";
import { replaceDeep, navigate } from "../Layout/Router";
import { PageContainer, SectionHeader, SelectorBar, SelectField, DataTable, FormDialog, formatDate } from "../Layout/Common";
import { AttachmentList, compressImage } from "../Layout/Attachments";
import CountryPicker from "../Layout/CountryPicker";
import RichTextEditor from "../Layout/RichText/RichTextEditor";
import { plainText } from "../Layout/RichText/markdown";

// The in-app news feed of a title (the backend's News module): one list per branch, edited here, synced to the
// players inside their session start as a delta. Likes are the players' (public in the app); views are only here.
// An item's title and body are localization tags (the folder news/items/<branch>/<key> of the Localization page,
// on demand: the feed carries the player's language); News requires Localization for that.
// URL: /<game>/news[/<branch>[/<key>]].
const news = api.group("news");
const locApi = api.group("localization");
const TIER_LABEL = ["none", "AI isolated", "AI context", "human generic", "human revised", "human specialized"];
const UNSET = "0001-01-01T00:00:00Z";
const MAX_PATCH = 0x1FFF;
// The news header slot is 4:1 (the game's shipped headers are 512x128); anything wider is downscaled before upload
const IMAGE_WIDTH = 1024, IMAGE_MAX_BYTES = 1024 * 1024;

const isSet = (iso) => !!iso && !iso.startsWith("0001-");
// ISO (UTC) -> the value of a datetime-local input (local time), and back
const toLocalInput = (iso) => {
    if (!isSet(iso)) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v) => (v ? new Date(v).toISOString() : UNSET);

const emptyItem = () => ({
    key: "", title: "", body: "", image: "", date: new Date().toISOString(), showFrom: UNSET, showUntil: UNSET,
    mustShowOnStart: false, countries: [], minVersion: 0, maxVersion: 0,
});

export default class NewsPage extends React.Component {
    state = {
        branch: "", version: 0, items: [], loading: false,
        clientVersions: [],           // [{ label: "0.16", major, minor }] of the selected branch (the versioning list)
        dialog: null,                 // { mode: "create" | "update", item, error, autoTranslate }
        deleteKey: null, uploading: false,
        trLanguages: [],              // the localization project's target languages (the Translations block); [] = no service
        trLang: null, trDraft: null,  // the language shown and its edit buffer { lang, title, body }
    };

    fileInput = React.createRef();

    componentDidMount() {
        const deep = globals.route?.deep ?? [];
        const branch = deep[0] && globals.branches.includes(deep[0]) ? deep[0] : "Development";
        this.setState({ branch }, () => {
            this.loadVersions().finally(() => this.load(deep[1]));
        });
        locApi("Loading languages", ["project"], { silent: true })
            .then(p => { if (p && typeof p !== "string") this.setState({ trLanguages: (p.languages ?? []).map(l => l.code).filter(c => c !== p.baseLanguage) }); })
            .catch(() => {});
    }

    // ---------- roles ----------
    get canEdit() {
        if (globals.loginData?.titles?.isMaster) return true;
        const raw = globals.loginData?.titles?.roles?.[globals.selectedTitle];
        const roles = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? Object.keys(raw) : [];
        return roles.includes("news");
    }

    // ---------- loads ----------
    loadVersions = () => {
        return api.versioning("Loading versions", ["list"], { silent: true })
            .then(list => {
                const branchIndex = globals.branches.indexOf(this.state.branch);
                const entry = Array.isArray(list) ? list.find(b => b.branch === branchIndex) : null;
                const seen = new Set();
                const versions = [];
                (entry?.versions ?? []).forEach(v => {
                    const { major, minor } = globals.decompactVersion(v.version);
                    const label = `${major}.${minor}`;
                    if (seen.has(label)) return;
                    seen.add(label);
                    versions.push({ label, major, minor });
                });
                versions.sort((a, b) => (b.major - a.major) || (b.minor - a.minor));
                this.setState({ clientVersions: versions });
            })
            .catch(() => this.setState({ clientVersions: [] }));
    }

    load = (openKey) => {
        const { branch } = this.state;
        this.setState({ loading: true });
        return news("Loading news", ["list", branch], { silent: true })
            .then(r => {
                if (!r || typeof r === "string" || !r.items) { this.setState({ items: [], version: 0 }); return; }
                const items = [...r.items].sort((a, b) => (b.date ?? "").localeCompare(a.date ?? ""));
                this.setState({ items, version: r.version ?? 0 });
                if (openKey) {
                    const item = items.find(i => i.key === openKey);
                    if (item) this.openEdit(item);
                }
            })
            .catch(() => {})
            .finally(() => this.setState({ loading: false }));
    }

    changeBranch = (branch) => {
        this.setState({ branch, items: [], version: 0 }, () => {
            replaceDeep([branch]);
            this.loadVersions().finally(() => this.load());
        });
    }

    // ---------- versions ----------
    // The gate the backend applies is on the client's compact version (branch, major, minor, patch): a minimum of
    // "0.16" is 0.16.0, a maximum of "0.16" is every 0.16 patch
    versionOptions(kind) {
        const branchIndex = globals.branches.indexOf(this.state.branch);
        const opts = [{ value: 0, label: kind === "min" ? "Any (no minimum)" : "Any (no maximum)" }];
        this.state.clientVersions.forEach(v => {
            const value = globals.compactVersion(v.major, v.minor, kind === "min" ? 0 : MAX_PATCH, branchIndex);
            opts.push({ value, label: v.label });
        });
        return opts;
    }

    versionLabel(compact) {
        if (!compact) return null;
        const { major, minor } = globals.decompactVersion(compact);
        return `${major}.${minor}`;
    }

    // ---------- dialog ----------
    openCreate = () => this.setState({ dialog: { mode: "create", item: emptyItem(), error: null } });

    openEdit = (item) => {
        this.setState({ dialog: { mode: "update", item: { ...item, countries: [...(item.countries ?? [])] }, error: null } });
        replaceDeep([this.state.branch, item.key]);
    }

    closeDialog = () => {
        this.setState({ dialog: null });
        replaceDeep([this.state.branch]);
    }

    setField = (field, value) => {
        this.setState(s => ({ dialog: { ...s.dialog, item: { ...s.dialog.item, [field]: value }, error: null } }));
    }

    save = () => {
        const { dialog, branch } = this.state;
        const item = dialog.item;
        const payload = {
            key: (item.key ?? "").trim(), title: (item.title ?? "").trim(), body: item.body ?? "", image: item.image ?? "",
            date: item.date ?? UNSET, showFrom: item.showFrom ?? UNSET, showUntil: item.showUntil ?? UNSET,
            mustShowOnStart: !!item.mustShowOnStart,
            countries: (item.countries ?? []).map(c => String(c).trim().toUpperCase()).filter(c => c.length === 2),
            minVersion: item.minVersion ?? 0, maxVersion: item.maxVersion ?? 0,
            autoTranslate: dialog.autoTranslate !== false,
        };
        const parts = dialog.mode === "create" ? ["create", branch] : ["update", branch, item.key];
        news(dialog.mode === "create" ? "Creating news" : "Saving news", parts, { payload, retry: false, timeout: 90000 })
            .then(r => {
                if (typeof r === "string") { this.setState(s => ({ dialog: { ...s.dialog, error: r } })); return; }
                if (r?.job) enqueueSnackbar(typeof r.job === "string" ? `Translation not started: ${r.job}` : r.job.nothing ? r.job.message : r.job.status === "done" ? `Translated: ${r.job.summary}` : `Translation ${r.job.status} (${r.job.unitsTotal} units)`, { variant: "info" });
                if (r?.item) this.setState(s => (s.dialog ? { dialog: { ...s.dialog, item: { ...s.dialog.item, translations: r.item.translations } } } : null));
                if (dialog.mode === "create") {
                    // stay in the dialog as an update, so the image can be uploaded right away
                    this.setState(s => ({ dialog: { ...s.dialog, mode: "update", error: null } }));
                    replaceDeep([branch, payload.key]);
                } else this.closeDialog();
                this.load();
            })
            .catch(() => {});
    }

    confirmDelete = () => {
        const { deleteKey, branch } = this.state;
        news("Deleting news", ["delete", branch, deleteKey])
            .then(r => {
                if (typeof r === "string") enqueueSnackbar(r, { variant: "error" });
                this.setState({ deleteKey: null });
                this.load();
            })
            .catch(() => this.setState({ deleteKey: null }));
    }

    pickImage = () => this.fileInput.current?.click();

    uploadImage = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        const { dialog, branch } = this.state;
        if (!file || !dialog || dialog.mode !== "update") return;
        this.setState({ uploading: true });
        // downscale to the standard width and keep it small; the backend replaces (and deletes) the previous image
        this.scaleImage(file)
            .then(scaled => news("Uploading the image", ["upload-image", branch, dialog.item.key], { payload: scaled }))
            .then(r => {
                if (typeof r === "string") { this.setState(s => ({ dialog: { ...s.dialog, error: r } })); return; }
                if (r?.url) this.setField("image", r.url);
                this.load();
            })
            .catch(err => { if (err?.message) this.setState(s => ({ dialog: { ...s.dialog, error: `Image: ${err.message}` } })); })
            .finally(() => this.setState({ uploading: false }));
    }

    async scaleImage(file) {
        if (!/^image\/(png|jpeg)$/.test(file.type)) return file;   // gif / webp go as they are (size checked by the backend)
        let width = 0;
        try { const bmp = await createImageBitmap(file); width = bmp.width; bmp.close?.(); } catch { }
        const scale = width > IMAGE_WIDTH ? IMAGE_WIDTH / width : 1;
        if (scale === 1 && file.size <= IMAGE_MAX_BYTES) return file;
        return compressImage(file, IMAGE_MAX_BYTES, scale);
    }

    // ---------- translations (milestone 2): the item per language, AI-written or edited here ----------
    saveTranslation = (lang, draft) => {
        const { dialog, branch } = this.state;
        news(`Saving the ${lang} translation`, ["translation", branch, dialog.item.key, lang], { payload: { title: draft.title, body: draft.body } })
            .then(r => {
                if (typeof r === "string") { this.setState(s => ({ dialog: { ...s.dialog, error: r } })); return; }
                this.setState(s => ({ trDraft: null, dialog: { ...s.dialog, item: { ...s.dialog.item, translations: { ...(s.dialog.item.translations ?? {}), [lang]: r.translation } } } }));
                this.load();
            }).catch(() => {});
    }

    retranslate = () => {
        const { dialog, branch } = this.state;
        news("Retranslating", ["retranslate", branch, dialog.item.key], { retry: false, timeout: 90000 })
            .then(r => {
                if (typeof r === "string") { this.setState(s => ({ dialog: { ...s.dialog, error: r } })); return; }
                if (r.nothing) { enqueueSnackbar(r.message, { variant: "info" }); return; }
                enqueueSnackbar(r.status === "done" ? r.summary : `Translation ${r.status}: ${r.unitsTotal} units, the feed updates as they land`, { variant: "info" });
                setTimeout(() => this.refreshDialogItem(), r.status === "done" ? 500 : 20000);
            }).catch(() => {});
    }

    refreshDialogItem = () => {
        const { dialog, branch } = this.state;
        if (!dialog) return;
        news("Refreshing", ["get", branch, dialog.item.key], { silent: true })
            .then(r => { if (r?.item) this.setState(s => (s.dialog ? { trDraft: null, dialog: { ...s.dialog, item: { ...s.dialog.item, translations: r.item.translations } } } : null)); this.load(); })
            .catch(() => {});
    }

    renderTranslations(it) {
        const { trLanguages, trLang, trDraft } = this.state;
        const langs = trLanguages.length ? trLanguages : Object.keys(it.translations ?? {});
        if (langs.length === 0) return null;
        const lang = trLang && langs.includes(trLang) ? trLang : langs[0];
        const t = it.translations?.[lang];
        const draft = trDraft?.lang === lang ? trDraft : { lang, title: t?.title ?? "", body: t?.body ?? "" };
        const setDraft = (patch) => this.setState({ trDraft: { ...draft, ...patch } });
        const mark = (l) => { const x = it.translations?.[l]; return !x ? "" : x.outdated ? " !" : " ✓"; };
        return (
            <Box sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="subtitle2">Translations</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>✓ current, ! outdated (the base text changed; players see the base text until it is redone). The title and the body are the localization tags of the folder shown.</Typography>
                    {it.folder && <Button size="small" onClick={() => navigate({ title: globals.selectedTitle, page: "localization", deep: ["strings", ...it.folder.split("/")] })}>Open in Localization</Button>}
                    <Button size="small" onClick={this.retranslate}>Retranslate all (AI)</Button>
                </Box>
                <Tabs value={lang} onChange={(_, v) => this.setState({ trLang: v, trDraft: null })} variant="scrollable" scrollButtons="auto" sx={{ minHeight: 32 }}>
                    {langs.map(l => <Tab key={l} value={l} label={l + mark(l)} sx={{ minWidth: 52, minHeight: 32, py: 0 }} />)}
                </Tabs>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", my: 1 }}>
                    {t ? <>
                        <Chip size="small" label={TIER_LABEL[t.tier] ?? t.tier} />
                        {t.outdated && <Chip size="small" color="warning" label="outdated" />}
                        <Typography variant="caption" color="text.secondary">{t.updatedBy} · {formatDate(t.updatedDate)}</Typography>
                    </> : <Typography variant="caption" color="text.secondary">No {lang} translation yet — the players in {lang} read the base text.</Typography>}
                </Box>
                <TextField label={`Title (${lang})`} value={draft.title} onChange={e => setDraft({ title: e.target.value })} size="small" fullWidth sx={{ mb: 1 }} />
                <RichTextEditor key={lang} value={draft.body} onChange={md => setDraft({ body: md })} minHeight={120} placeholder={`The body in ${lang}`} />
                <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => this.saveTranslation(lang, draft)} disabled={trDraft?.lang !== lang || !draft.title.trim()}>Save {lang} (human revised)</Button>
                </Box>
            </Box>
        );
    }

    renderDialog() {
        const { dialog, uploading } = this.state;
        if (!dialog) return null;
        const it = dialog.item;
        const create = dialog.mode === "create";
        const field = (label, key, extra = {}) => (
            <TextField label={label} value={it[key] ?? ""} onChange={e => this.setField(key, e.target.value)} size="small" fullWidth {...extra} />
        );
        return (
            <FormDialog open title={create ? "New news item" : `Edit “${it.key}”`} onClose={this.closeDialog} onConfirm={this.save}
                        confirmText={create ? "Create" : "Save"} confirmDisabled={uploading} maxWidth="md" fullWidth>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    {dialog.error && <Alert severity="error">{dialog.error}</Alert>}
                    <Box sx={{ display: "flex", gap: 2 }}>
                        {field("Key", "key", { disabled: !create, helperText: create ? "1-64 letters, digits, _ - ." : undefined, sx: { maxWidth: 260 } })}
                        {field("Title", "title")}
                    </Box>
                    <RichTextEditor value={it.body ?? ""} onChange={md => this.setField("body", md)} minHeight={180} placeholder="The news body: headers, lists, positive / negative / highlight styles" />
                    {!create && this.renderTranslations(it)}
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        <TextField label="Date" type="datetime-local" size="small" value={toLocalInput(it.date)} InputLabelProps={{ shrink: true }}
                                   onChange={e => this.setField("date", fromLocalInput(e.target.value))} helperText="Shown to the players, sorts the list" />
                        <TextField label="Show from" type="datetime-local" size="small" value={toLocalInput(it.showFrom)} InputLabelProps={{ shrink: true }}
                                   onChange={e => this.setField("showFrom", fromLocalInput(e.target.value))} helperText="Optional" />
                        <TextField label="Show until" type="datetime-local" size="small" value={toLocalInput(it.showUntil)} InputLabelProps={{ shrink: true }}
                                   onChange={e => this.setField("showUntil", fromLocalInput(e.target.value))} helperText="Optional" />
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                        <SelectField label="Min version" size="small" minWidth={200} value={it.minVersion ?? 0} options={this.versionOptions("min")}
                                     onChange={v => this.setField("minVersion", v)} />
                        <SelectField label="Max version" size="small" minWidth={200} value={it.maxVersion ?? 0} options={this.versionOptions("max")}
                                     onChange={v => this.setField("maxVersion", v)} />
                        <CountryPicker value={it.countries ?? []} onChange={v => this.setField("countries", v)} sx={{ minWidth: 320, flex: 1 }} />
                        <FormControlLabel control={<Checkbox checked={!!it.mustShowOnStart} onChange={e => this.setField("mustShowOnStart", e.target.checked)} />}
                                          label="Pop up at start" />
                        {this.state.trLanguages.length > 0 && <FormControlLabel control={<Checkbox checked={dialog.autoTranslate !== false} onChange={e => this.setState(s => ({ dialog: { ...s.dialog, autoTranslate: e.target.checked } }))} />}
                                          label="AI translates a changed title or body" />}
                    </Box>
                    <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                        <Button variant="outlined" startIcon={<UploadIcon />} onClick={this.pickImage} disabled={uploading || create} sx={{ whiteSpace: "nowrap" }}>
                            {uploading ? "Uploading…" : isSet(it.image) ? "Replace image" : "Upload image"}
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                            {create ? "Create the item first, then upload its image." : "Recommended 1024×256 (4:1), PNG or JPEG; larger images are downscaled. Uploading again replaces the previous image."}
                        </Typography>
                        <input ref={this.fileInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={this.uploadImage} />
                    </Box>
                    {isSet(it.image) && it.image.startsWith("https://") &&
                        <AttachmentList attachments={[{ name: it.image.split("/").pop(), url: it.image, type: "image/*" }]} height={120} />}
                </Box>
            </FormDialog>
        );
    }

    render() {
        const { branch, version, items, loading, deleteKey } = this.state;
        const canEdit = this.canEdit;
        const columns = [
            { id: "key", label: "Key", render: n => <span style={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}>{n.key}</span> },
            { id: "title", label: "Title", render: n => (
                <Box>
                    <Box>{n.title}</Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{plainText(n.body, 90)}</Typography>
                </Box>
            ) },
            { id: "date", label: "Date", render: n => formatDate(n.date) },
            { id: "window", label: "Window", render: n => isSet(n.showFrom) || isSet(n.showUntil)
                ? `${isSet(n.showFrom) ? formatDate(n.showFrom) : "…"} → ${isSet(n.showUntil) ? formatDate(n.showUntil) : "…"}` : "always" },
            { id: "start", label: "Start", render: n => n.mustShowOnStart ? <Chip size="small" color="info" label="pop-up" /> : "" },
            { id: "countries", label: "Countries", render: n => (n.countries ?? []).length ? n.countries.join(", ") : "all" },
            { id: "versions", label: "Versions", render: n => {
                const min = this.versionLabel(n.minVersion), max = this.versionLabel(n.maxVersion);
                return !min && !max ? "all" : `${min ?? "…"} – ${max ?? "…"}`;
            }},
            { id: "image", label: "Image", render: n => isSet(n.image) ? <Chip size="small" label="yes" component="a" href={n.image} target="_blank" clickable /> : "" },
            { id: "likes", label: "Likes", align: "right", render: n => n.likes ?? 0 },
            { id: "views", label: "Views", align: "right", render: n => n.views ?? 0 },
            { id: "feed", label: "Feed v", align: "right", render: n => n.feedVersion ?? "" },
            { id: "updated", label: "Updated", render: n => <Tooltip title={n.updatedBy ?? ""}><span>{formatDate(n.updatedDate)}</span></Tooltip> },
        ];
        if (canEdit) columns.push({ id: "actions", label: "", align: "right", render: n => (
            <Box sx={{ whiteSpace: "nowrap" }}>
                <IconButton size="small" onClick={() => this.openEdit(n)}><EditIcon fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => this.setState({ deleteKey: n.key })}><DeleteIcon fontSize="small" /></IconButton>
            </Box>
        )});

        return (
            <PageContainer maxWidth={1300}>
                <SectionHeader title="News">
                    <Chip size="small" label={`Feed version ${version}`} />
                    <IconButton size="small" onClick={() => this.load()} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
                    {canEdit && <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={this.openCreate}>New item</Button>}
                </SectionHeader>
                <SelectorBar>
                    <SelectField label="Branch" size="small" minWidth={180} value={branch} options={globals.branches} onChange={this.changeBranch} />
                    <Typography variant="body2" color="text.secondary">
                        {items.length} item{items.length === 1 ? "" : "s"}. Players receive the feed inside their session start; a change reaches
                        them at their next boot. Likes are public in the app, views are only counted here.
                    </Typography>
                </SelectorBar>
                <DataTable columns={columns} rows={items} getRowKey={n => n.key} />
                {this.renderDialog()}
                <FormDialog open={!!deleteKey} title="Delete news item" onClose={() => this.setState({ deleteKey: null })} onConfirm={this.confirmDelete} confirmText="Delete">
                    <Typography>Delete “{deleteKey}” from the {branch} feed? Players who have it drop it at their next boot.</Typography>
                </FormDialog>
            </PageContainer>
        );
    }
}
